import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import EntityFormDialog from "@/components/EntityFormDialog";
import { toast } from "sonner";
import { ListChecks, Plus, Search, Loader2, Pencil, Trash2, Camera, GripVertical, X, Clock } from "lucide-react";

const CATEGORIE = [
  "reception", "camere", "hall", "bagni_comuni", "colazione", "aree_esterne", "ascensori",
  "corridoi", "personale", "housekeeping", "front_office", "back_office", "locali_tecnici", "standard_generale",
];
const FREQUENZE = ["giornaliera", "settimanale", "mensile", "personalizzata"];
const MAX_SLOTS = 4;

const HHMM_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

const EMPTY = {
  nome: "",
  categoria: "standard_generale",
  frequenza: "giornaliera",
  ruolo_target: "resident_manager",
  checklist_items: [{ label: "", obbligatorio: true, richiede_foto: false }],
  slots: [],
  attivo: true,
};

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterFreq, setFilterFreq] = useState("all");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setTemplates(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => templates.filter((t) => {
    if (filterCat !== "all" && t.categoria !== filterCat) return false;
    if (filterFreq !== "all" && t.frequenza !== filterFreq) return false;
    if (q && !t.nome.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [templates, q, filterCat, filterFreq]);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    // Normalize slots: hide 'default' single-slot in the UI (empty array = single-slot default)
    const displaySlots = (t.slots || []).filter((s) => s.ora);
    setForm({ ...t, slots: displaySlots });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const validItems = form.checklist_items.filter((i) => i.label && i.label.trim());
    if (validItems.length === 0) { toast.error("Aggiungi almeno un item alla checklist"); return; }
    // Slot validation (client-side, backend re-valides)
    const rawSlots = (form.slots || []).filter((s) => (s.label || "").trim() || s.ora);
    for (const s of rawSlots) {
      if (!(s.label || "").trim()) { toast.error("Ogni slot deve avere una label"); return; }
      if (!HHMM_RE.test(s.ora || "")) { toast.error(`Slot '${s.label}': ora deve essere HH:MM`); return; }
      if (s.deadline_ora && !HHMM_RE.test(s.deadline_ora)) { toast.error(`Slot '${s.label}': deadline deve essere HH:MM`); return; }
      if (s.ora && s.deadline_ora && s.deadline_ora <= s.ora) {
        toast.error(`Slot '${s.label}': deadline (${s.deadline_ora}) deve essere > ora (${s.ora})`); return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        checklist_items: validItems,
        // Empty slots array → backend treats as single-slot default
        slots: rawSlots.length > 0 ? rawSlots : undefined,
      };
      if (editId) {
        await api.patch(`/templates/${editId}`, payload);
        toast.success("Template aggiornato");
      } else {
        await api.post("/templates", payload);
        toast.success("Template creato");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Disattivare il template?")) return;
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Template disattivato");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const addItem = () => setForm((f) => ({ ...f, checklist_items: [...f.checklist_items, { label: "", obbligatorio: true, richiede_foto: false }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, checklist_items: f.checklist_items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, key, val) => setForm((f) => ({ ...f, checklist_items: f.checklist_items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));

  const addSlot = () => setForm((f) => {
    const cur = f.slots || [];
    if (cur.length >= MAX_SLOTS) { toast.error(`Massimo ${MAX_SLOTS} slot per template`); return f; }
    const defaults = cur.length === 0
      ? { label: "Mattina", ora: "09:00", deadline_ora: "12:00" }
      : cur.length === 1
      ? { label: "Pomeriggio", ora: "16:00", deadline_ora: "20:00" }
      : { label: "", ora: "", deadline_ora: "20:00" };
    return { ...f, slots: [...cur, defaults] };
  });
  const removeSlot = (idx) => setForm((f) => ({ ...f, slots: (f.slots || []).filter((_, i) => i !== idx) }));
  const updateSlot = (idx, key, val) => setForm((f) => ({
    ...f,
    slots: (f.slots || []).map((s, i) => i === idx ? { ...s, [key]: val } : s),
  }));

  return (
    <div className="space-y-6 ih-anim-in" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-2">
            <ListChecks className="h-3 w-3" /> Standard di qualità
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">
            Template <span className="font-medium">controlli</span>
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">{loading ? "Caricamento…" : `${filtered.length} template`}</p>
        </div>
        <Button onClick={openCreate} data-testid="new-template-btn"><Plus className="h-4 w-4 mr-1.5" /> Nuovo template</Button>
      </div>

      <div className="ih-glass rounded-2xl p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca template…" className="pl-9 h-10 bg-background/60" data-testid="templates-search" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-full sm:w-[220px] h-10"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterFreq} onValueChange={setFilterFreq}>
          <SelectTrigger className="w-full sm:w-[180px] h-10"><SelectValue placeholder="Frequenza" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le frequenze</SelectItem>
            {FREQUENZE.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="ih-glass rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border/60">
              <TableHead>Nome</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Frequenza</TableHead>
              <TableHead>Ruolo</TableHead>
              <TableHead>Slot</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Nessun template.</TableCell></TableRow>
            ) : (
              filtered.map((t) => (
                <TableRow key={t.id} className="border-border/40 hover:bg-accent/30" data-testid={`template-row-${t.id}`}>
                  <TableCell className="font-medium">{t.nome}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{t.categoria.replaceAll("_", " ")}</TableCell>
                  <TableCell><span className="px-2 py-0.5 rounded-full text-[10px] bg-accent/60 uppercase tracking-wider">{t.frequenza}</span></TableCell>
                  <TableCell className="text-xs">{ROLE_LABELS[t.ruolo_target] || t.ruolo_target}</TableCell>
                  <TableCell className="text-xs">
                    {(() => {
                      const active = (t.slots || []).filter((s) => s.ora);
                      if (active.length === 0) return <span className="text-muted-foreground">—</span>;
                      return (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-lavender-100/70 dark:bg-lavender-900/40 text-lavender-700 dark:text-lavender-300">
                          <Clock className="h-3 w-3" /> {active.length}× {active.map((s) => s.ora).join(" · ")}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-xs">{t.checklist_items?.length || 0}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${t.attivo ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`}>{t.attivo ? "Attivo" : "Disattivo"}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`edit-template-${t.id}`} size="sm" variant="ghost" onClick={() => openEdit(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`delete-template-${t.id}`} size="sm" variant="ghost" onClick={() => remove(t.id)}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <EntityFormDialog
        open={open}
        onOpenChange={setOpen}
        title={editId ? "Modifica template" : "Nuovo template"}
        description="Definisci checklist e regole."
        size="lg"
        scrollable
        onSubmit={submit}
        submitLabel={editId ? "Salva" : "Crea template"}
        saving={saving}
        testId="template-form"
        submitTestId="template-form-submit"
      >
        <div className="space-y-2"><Label>Nome</Label><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="template-form-nome" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Categoria</Label>
            <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Frequenza</Label>
            <Select value={form.frequenza} onValueChange={(v) => setForm({ ...form, frequenza: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{FREQUENZE.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Ruolo target</Label>
          <Select value={form.ruolo_target} onValueChange={(v) => setForm({ ...form, ruolo_target: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(ROLE_LABELS).filter(([k]) => k !== "read_only" && k !== "super_admin").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Slot orari — multi-slot task */}
        <div className="space-y-2" data-testid="template-slots-section">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Slot orari</Label>
            <Button
              type="button" variant="outline" size="sm"
              onClick={addSlot}
              disabled={(form.slots || []).length >= MAX_SLOTS}
              data-testid="add-slot-btn"
            >
              <Plus className="h-3 w-3 mr-1" /> Aggiungi slot
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Nessuno slot = single-slot (comportamento default). Aggiungi 2+ slot per task ripetute nella giornata
            (es. Mattina 09:00→12:00 · Pomeriggio 16:00→20:00). Max {MAX_SLOTS}.
          </p>
          <div className="space-y-2">
            {(form.slots || []).map((s, idx) => (
              <div
                key={idx}
                data-testid={`slot-row-${idx}`}
                className="rounded-xl border border-border/60 p-3 bg-background/40 grid grid-cols-[24px_1fr_110px_130px_36px] gap-2 items-center"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <Input
                  value={s.label || ""} placeholder="Label (es. Mattina)"
                  onChange={(e) => updateSlot(idx, "label", e.target.value)}
                  data-testid={`slot-${idx}-label`}
                />
                <Input
                  value={s.ora || ""} placeholder="09:00" maxLength={5}
                  onChange={(e) => updateSlot(idx, "ora", e.target.value)}
                  data-testid={`slot-${idx}-ora`}
                />
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">entro</span>
                  <Input
                    value={s.deadline_ora || ""} placeholder="20:00" maxLength={5}
                    onChange={(e) => updateSlot(idx, "deadline_ora", e.target.value)}
                    data-testid={`slot-${idx}-deadline`}
                  />
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeSlot(idx)} data-testid={`slot-${idx}-remove`}>
                  <X className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Items checklist</Label>
            <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-3 w-3 mr-1" /> Aggiungi</Button>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {form.checklist_items.map((it, idx) => (
              <div key={idx} className="rounded-xl border border-border/60 p-3 bg-background/40">
                <div className="flex items-start gap-2">
                  <GripVertical className="h-4 w-4 text-muted-foreground mt-2" />
                  <div className="flex-1 space-y-2">
                    <Input value={it.label} onChange={(e) => updateItem(idx, "label", e.target.value)} placeholder={`Item ${idx + 1}`} />
                    <div className="flex gap-4 text-xs">
                      <label className="flex items-center gap-2">
                        <Switch checked={!!it.obbligatorio} onCheckedChange={(v) => updateItem(idx, "obbligatorio", v)} /> Obbligatorio
                      </label>
                      <label className="flex items-center gap-2">
                        <Switch checked={!!it.richiede_foto} onCheckedChange={(v) => updateItem(idx, "richiede_foto", v)} /> <Camera className="h-3 w-3" /> Richiede foto
                      </label>
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)}><X className="h-4 w-4 text-rose-500" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </EntityFormDialog>
    </div>
  );
}
